import { Form, Formik } from "formik";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import {
  DemandPriorityType,
  DemandStateType,
  IDemand,
  IDemandConversationMessage,
  IDemandConversationMessages,
} from "../../models/demand";
import { IItemIdName } from "../../models/common";
import { IApplicationState } from "../../store";
import { StoreState } from "../../store/storeState";
import {
  getDemand,
  selectDemand,
  selectDemandState,
  updateDemand,
} from "../../store/demand";
import { FormButton } from "../../styles/button";
import {
  ApiError,
  FormGroup,
  InputContainer,
  InputRow,
} from "../../styles/form";
import Input from "../common/form/Input";
import Loader from "../common/Loader";
import * as Yup from "yup";
import demandApi from "../../api/demand";
import { SpaceBetweenButtons } from "../../styles/spaces";
import { RightType } from "../../models/auth";
import { hasClientWriteRight } from "../../utils/rights";
import { selectIdentityId, selectIdentityRights } from "../../store/identity";
import SuggestionFormik from "../common/suggestion/SuggestionFormik";
import SuggestionListFormik from "../common/suggestion/SuggestionListFormik";
import {
  handleLoadGroupSuggestions,
  handleLoadRoleSuggestions,
  handleLoadUserSuggestions,
} from "../../utils/suggestions";
import { format } from "date-fns";
import { promiseToastDelete, promiseToastSave } from "../../utils/toasts";
import SubmitForm from "../common/form/SubmitForm";
import { errorSet } from "../../utils/error";
import { DATE_TIME_FORMAT } from "../../utils/consts";
import { InputLabel } from "../common/form/InputStyles";
import { getClassByDate } from "../../utils/date";
import { clientDemandListLoad } from "../../store/clientDemandList";
import { WorkflowSelectedQuestions } from "../../models/workflow";
import {
  getSelectedAnswers,
  isAllQuestionsSelected,
  renderQuestions,
} from "../demand/DemandQuestions";
import {
  ChatHistory,
  ChatMessageActions,
  ChatMessageCard,
  ChatMessageMeta,
  ChatMessageRow,
  ChatMessageText,
  ChatRecipients,
  ChatSection,
  ChatTitle,
} from "./ClientDemandDetailStyles";
import { ModalYesNoFunction } from "../common/modal/ModalFunctions";
import { GridIconDelete } from "../common/grid/GridIcons";

interface IClientDemandDetailFormValues
  extends Omit<IDemand, "conversationMessage"> {
  conversationMessage: IDemandConversationMessage;
  conversationMessageUsers: IItemIdName[];
  conversationMessageRequestedRoleName: string;
}

interface IProps {
  demandState: StoreState;
  demand: IDemand | null;
  identityId?: number;
  identityRights?: RightType[];
  getDemand(demandId: number): void;
  updateDemand(newData: IDemand): void;
  clientDemandListLoad(reload: boolean): void;
}

const ClientDemandDetail: FC<IProps> = ({
  demandState,
  demand,
  identityId,
  identityRights,
  getDemand,
  updateDemand,
  clientDemandListLoad,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId: paramClientId, demandId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuestions, setSelectedQuestions] =
    useState<WorkflowSelectedQuestions>({});

  useEffect(() => {
    getDemand(Number.parseInt(demandId!));
    setLoading(false);
  }, [demandId, getDemand, updateDemand]);

  const getMessageRecipients = (
    message: IDemandConversationMessages
  ): string[] => {
    if (!message.sentUsers.length) return [];

    return [
      `${t("demand.taggedUsers")}: ${message.sentUsers
        .map(({ name }) => name)
        .join(", ")}`,
    ];
  };

  const handleDeleteConversationMessage = async (messageId: number) => {
    const confirmed = await ModalYesNoFunction(
      t("demand.deleteConversationMessage"),
      t("demand.deleteConversationMessageConfirm")
    );
    if (!confirmed) return;

    setError(null);
    setSaving(true);
    try {
      await promiseToastDelete(async () => {
        await demandApi.deleteConversationMessage(demand!.id, messageId);
        const refreshedDemand = await demandApi.getDemand(demand!.id);
        updateDemand(refreshedDemand.data);
      });
    } catch (err) {
      errorSet(setError, err, t);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (data: IClientDemandDetailFormValues) => {
    setError(null);
    try {
      await promiseToastSave(async () => {
        const {
          conversationMessageUsers,
          conversationMessageRequestedRoleName,
          ...formData
        } = data;
        const conversationMessageUserIds =
          conversationMessageUsers.length > 0
            ? conversationMessageUsers.map((user) => user.id)
            : null;
        const conversationMessageRequestedRoleId =
          formData.conversationMessage.requestedRoleId &&
          formData.conversationMessage.requestedRoleId > 0
            ? formData.conversationMessage.requestedRoleId
            : null;
        const conversationMessage: IDemandConversationMessage = {
          ...formData.conversationMessage,
          text: formData.conversationMessage.text.trim(),
          userIds: conversationMessageUserIds,
          requestedRoleId: conversationMessageRequestedRoleId,
        };
        const hasConversationMessage =
          conversationMessage.text.length > 0 ||
          !!conversationMessage.userIds?.length ||
          conversationMessage.requestedRoleId !== null;

        const data2: IDemand = {
          ...formData,
          assignedUserId: formData.assignedUserId
            ? formData.assignedUserId
            : null,
          assignedGroupId: formData.assignedGroupId
            ? formData.assignedGroupId
            : null,
          clientId: formData.clientId ? formData.clientId : null,
          conversationMessage: hasConversationMessage
            ? conversationMessage
            : null,
        };

        await demandApi.updateDemand(data2);
        clientDemandListLoad(true);
        navigate(`/client/${paramClientId}/demand`);
      });
    } catch (err) {
      errorSet(setError, err, t);
    }
  };

  const handleCancel = () => {
    navigate(`/client/${paramClientId}/demand`);
  };

  const handleCancelDemand = async () => {
    setError(null);
    setSaving(true);
    try {
      await promiseToastSave(async () => {
        await demandApi.cancelDemand(demand!.id);
        clientDemandListLoad(true);
        navigate(`/client/${paramClientId}/demand`);
      });
    } catch (err) {
      errorSet(setError, err, t);
    }
    setSaving(false);
  };

  const handleCloseDemand = async () => {
    setError(null);
    setSaving(true);
    try {
      await promiseToastSave(async () => {
        const answers: number[] = [];
        getSelectedAnswers(
          answers,
          demand!.questions,
          selectedQuestions,
          demand!.idWorkflowDefinitionItem
        );

        await demandApi.closeDemand(demand!.id, answers);
        clientDemandListLoad(true);
        navigate(`/client/${paramClientId}/demand`);
      });
    } catch (err) {
      errorSet(setError, err, t);
    }
    setSaving(false);
  };

  const handleTakeDemand = async () => {
    setError(null);
    setSaving(true);
    try {
      await promiseToastSave(async () => {
        const newData = await demandApi.takeDemand(demand!.id);
        updateDemand(newData.data);
        clientDemandListLoad(true);
      });
    } catch (err) {
      errorSet(setError, err, t);
    }
    setSaving(false);
  };

  const handleHistory = () => {
    navigate(
      `/demand/${demandId}/history/?return=/client/${paramClientId}/demand/${demandId}`
    );
  };

  if (loading || demandState === StoreState.Loading) {
    return <Loader />;
  }

  if (demandState === StoreState.Error) {
    return t("errors.unknown");
  }

  const canCloseDemand = isAllQuestionsSelected(
    demand!.questions,
    selectedQuestions,
    demand!.idWorkflowDefinitionItem
  );
  const canEditDemand =
    demand!.state === DemandStateType.New &&
    hasClientWriteRight(identityRights, [RightType.WriteClientDemand]);

  return (
    <Formik<IClientDemandDetailFormValues>
      initialValues={{
        ...demand!,
        assignedUserId: demand!.assignedUserId ?? 0,
        assignedUserName: demand!.assignedUserName ?? "",
        assignedGroupId: demand!.assignedGroupId ?? 0,
        assignedGroupName: demand!.assignedGroupName ?? "",
        clientId: demand!.clientId ?? 0,
        clientName: demand!.clientName ?? "",
        conversationMessage: {
          text: "",
          userIds: [],
          requestedRoleId: null,
        },
        conversationMessageUsers: [],
        conversationMessageRequestedRoleName: "",
      }}
      validationSchema={Yup.object().shape(
        {
          assignedUserId: Yup.number().when("assignedGroupId", {
            is: (assignedGroupId: any) => !assignedGroupId,
            then: Yup.number()
              .required(t("demand.anyOfValuesRequired"))
              .moreThan(0, t("demand.anyOfValuesRequired")),
          }),
          assignedGroupId: Yup.number().when("assignedUserId", {
            is: (assignedUserId: any) => !assignedUserId,
            then: Yup.number()
              .required(t("demand.anyOfValuesRequired"))
              .moreThan(0, t("demand.anyOfValuesRequired")),
          }),
        },
        [["assignedUserId", "assignedGroupId"]]
      )}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => {
        const hasConversationMessageUsers =
          (values.conversationMessageUsers?.length ?? 0) > 0;
        const hasConversationMessageRequestedRole =
          (values.conversationMessage?.requestedRoleId ?? 0) > 0 ||
          (values.conversationMessageRequestedRoleName ?? "").trim().length > 0;

        return (
          <Form>
            {(demand!.state === DemandStateType.New &&
              !!demand!.assignedUserId && (
                <h1>{t("demand.detailTitleNew")}</h1>
              )) ||
              (demand!.state === DemandStateType.New &&
                !demand!.assignedUserId && (
                  <h1>{t("demand.detailTitleUnassigned")}</h1>
                )) ||
              (demand!.state === DemandStateType.Finished && (
                <h1>{t("demand.detailTitleFinished")}</h1>
              )) ||
              (demand!.state === DemandStateType.Canceled && (
                <h1>{t("demand.detailTitleCanceled")}</h1>
              ))}
            <FormGroup>
              <Input name="name" label={t("demand.name")} disabled={true} />
              <Input
                key="priority"
                name="priority"
                component="select"
                label={t("demand.priority")}
                disabled={true}
              >
                {(
                  Object.keys(DemandPriorityType) as Array<
                    keyof typeof DemandPriorityType
                  >
                ).map((key) => (
                  <option key={key} value={DemandPriorityType[key]}>
                    {t("demand.priorities." + DemandPriorityType[key])}
                  </option>
                ))}
              </Input>
              <InputRow>
                <InputLabel>{t("demand.deadLine")}</InputLabel>
                <InputContainer>
                  <span className={getClassByDate(demand!.deadLine)}>
                    {format(demand!.deadLine, DATE_TIME_FORMAT)}
                  </span>
                </InputContainer>
              </InputRow>
              <SuggestionFormik
                nameId="assignedUserId"
                nameText="assignedUserName"
                label={t("demand.assignedUser")}
                placeholder={
                  values.canChangeAssignedUser
                    ? undefined
                    : t("common.suggestionNoSelected")
                }
                disabled={!values.canChangeAssignedUser}
                loadSuggestions={handleLoadUserSuggestions}
              />
              <SuggestionFormik
                nameId="assignedGroupId"
                nameText="assignedGroupName"
                label={t("demand.assignedGroup")}
                placeholder={
                  values.canChangeAssignedGroup
                    ? undefined
                    : t("common.suggestionNoSelected")
                }
                disabled={!values.canChangeAssignedGroup}
                loadSuggestions={handleLoadGroupSuggestions}
              />
              {demand!.state === DemandStateType.New &&
                demand!.assignedUserId === identityId &&
                renderQuestions(
                  demand!.questions,
                  selectedQuestions,
                  setSelectedQuestions,
                  demand!.idWorkflowDefinitionItem
                )}
            </FormGroup>
            {error && <ApiError>{error}</ApiError>}
            <FormButton
              ver="secondary"
              disabled={isSubmitting || saving}
              onClick={handleCancel}
            >
              {t("common.back")}
            </FormButton>
            {canEditDemand && (
              <>
                <SpaceBetweenButtons />
                <SubmitForm disabled={saving} />
              </>
            )}
            {demand?.canTake && (
              <>
                <SpaceBetweenButtons />
                <FormButton
                  type="button"
                  disabled={isSubmitting || saving}
                  onClick={handleTakeDemand}
                >
                  {t("demand.take")}
                </FormButton>
              </>
            )}
            {demand!.state === DemandStateType.New &&
              demand!.assignedUserId === identityId && (
                <>
                  <SpaceBetweenButtons />
                  <FormButton
                    ver="secondary"
                    type="button"
                    disabled={isSubmitting || saving}
                    onClick={handleCancelDemand}
                  >
                    {t("demand.cancel")}
                  </FormButton>
                  <SpaceBetweenButtons />
                  <FormButton
                    type="button"
                    disabled={isSubmitting || saving || !canCloseDemand}
                    onClick={handleCloseDemand}
                  >
                    {t("demand.close")}
                  </FormButton>
                </>
              )}
            <SpaceBetweenButtons />
            <FormButton
              ver="secondary"
              type="button"
              disabled={isSubmitting || saving}
              onClick={handleHistory}
            >
              {t("demand.history")}
            </FormButton>
            <ChatSection>
              <ChatTitle>{t("demand.notes")}</ChatTitle>
              <ChatHistory>
                {demand!.conversationMessages.length > 0 &&
                  demand!.conversationMessages.map((message) => (
                    <ChatMessageRow key={message.id}>
                      <ChatMessageCard>
                        <ChatMessageMeta>
                          <strong>{message.createdByUser.name}</strong>
                          <span>
                            {format(message.createdAt, DATE_TIME_FORMAT)}
                          </span>
                        </ChatMessageMeta>
                        <ChatRecipients>
                          {getMessageRecipients(message)}
                        </ChatRecipients>
                        <ChatMessageText>
                          {message.text || "No text"}
                        </ChatMessageText>
                      </ChatMessageCard>
                      <ChatMessageActions>
                        {message.canDelete && (
                          <GridIconDelete
                            onClick={() =>
                              handleDeleteConversationMessage(message.id)
                            }
                          />
                        )}
                      </ChatMessageActions>
                    </ChatMessageRow>
                  ))}
              </ChatHistory>
              {canEditDemand && (
                <FormGroup labelWidth="12rem">
                  <Input
                    name="conversationMessage.text"
                    label={t("client.care.note")}
                    component="textarea"
                    inputHeight="8rem"
                    disabled={saving || isSubmitting}
                  />
                  <SuggestionListFormik
                    nameList="conversationMessageUsers"
                    nameId="id"
                    nameText="name"
                    label={t("calendar.list.users")}
                    loadSuggestions={handleLoadUserSuggestions}
                    disabled={
                      saving ||
                      isSubmitting ||
                      hasConversationMessageRequestedRole
                    }
                  />
                  <SuggestionFormik
                    nameId="conversationMessage.requestedRoleId"
                    nameText="conversationMessageRequestedRoleName"
                    label={t("admin.user.role")}
                    loadSuggestions={handleLoadRoleSuggestions}
                    disabled={
                      saving || isSubmitting || hasConversationMessageUsers
                    }
                  />
                </FormGroup>
              )}
            </ChatSection>
          </Form>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: IApplicationState) => {
  return {
    demandState: selectDemandState(state),
    demand: selectDemand(state),
    identityId: selectIdentityId(state),
    identityRights: selectIdentityRights(state),
  };
};

const mapDispachToProps = {
  getDemand,
  updateDemand,
  clientDemandListLoad,
};

export default connect(mapStateToProps, mapDispachToProps)(ClientDemandDetail);
